import { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  useSpacetimeDB,
  useTable,
  useReducer,
} from 'spacetimedb/react';
import { useHas, useQuery, useTrait, useWorld } from 'koota/react';
import type { Entity } from 'koota';
import { tables, reducers } from './module_bindings';
import {
  CameraPitch,
  CharacterIdentity,
  CharacterName,
  CharacterUserId,
  IsCharacter,
  IsLocal,
  IsSegment,
  Pose,
  SegmentGeometry,
  SegmentId,
  SegmentLinks,
  TouchJoystick,
  session,
} from './world';
import {
  clamp,
  radialOutward,
  raySegmentSaturationDirection,
  raycastSegmentTheta,
  segmentToWorld,
} from './segment';
import type { SegmentDef } from './segment';
import { scene } from './theme';
import {
  InGameMenu,
  LandingPage,
  SessionGuard,
  usePath,
  useSessionState,
} from './session';
import { ToolsRoute } from './tools/ToolsRoute';

// ---------------------------------------------------------------------------
// Bridges — the only place STDB hooks (useTable) and Koota writes meet
// (per ADR-0003 D5).
// ---------------------------------------------------------------------------

function SegmentBridge({
  segmentEntities,
}: {
  segmentEntities: Map<string, Entity>;
}) {
  const world = useWorld();
  const [segments] = useTable(tables.segment);

  // Cleanup on unmount: destroy every Koota entity we created so that a
  // subsequent mount (e.g. after Switch identity / Log out) starts from a
  // clean world. Without this, the Koota world (a module-level singleton)
  // accumulates orphan entities from previous sessions.
  useEffect(() => {
    return () => {
      for (const entity of segmentEntities.values()) {
        try {
          entity.destroy();
        } catch {
          /* ignore — entity may already be gone */
        }
      }
      segmentEntities.clear();
    };
  }, [segmentEntities]);

  useEffect(() => {
    const seen = new Set<string>();
    for (const seg of segments) {
      const id = seg.segmentId.toString();
      seen.add(id);
      const existing = segmentEntities.get(id);
      const geom = {
        p0X: seg.p0X, p0Y: seg.p0Y, p0Z: seg.p0Z,
        p1X: seg.p1X, p1Y: seg.p1Y, p1Z: seg.p1Z,
        p2X: seg.p2X, p2Y: seg.p2Y, p2Z: seg.p2Z,
        p3X: seg.p3X, p3Y: seg.p3Y, p3Z: seg.p3Z,
        length: seg.length,
        radiusBase: seg.radiusBase,
        radiusTip: seg.radiusTip,
      };
      const links = {
        parentSegmentId:
          seg.parentSegmentId === 0n ? '' : seg.parentSegmentId.toString(),
        treeId: seg.treeId.toString(),
        regionId: seg.regionId.toString(),
      };
      if (existing) {
        existing.set(SegmentGeometry, geom);
        existing.set(SegmentLinks, links);
      } else {
        const entity = world.spawn(
          SegmentGeometry(geom),
          SegmentLinks(links),
          SegmentId({ value: id })
        );
        entity.add(IsSegment);
        segmentEntities.set(id, entity);
      }
    }
    for (const [id, entity] of segmentEntities) {
      if (!seen.has(id)) {
        entity.destroy();
        segmentEntities.delete(id);
      }
    }
  }, [segments, world, segmentEntities]);

  return null;
}

/**
 * Bridge for the character table. Uses the user table to look up display
 * names by userId — the character row carries identity + userId, the user
 * row carries the name. Both are public so this join is trivial.
 *
 * IsLocal is added to the character whose identity matches conn.identity.
 * Per the project convention, we deliberately DO NOT overwrite Pose for
 * IsLocal entities — local input is authoritative.
 */
function CharacterBridge() {
  const conn = useSpacetimeDB();
  const myIdentity = conn?.identity?.toHexString() ?? null;
  const world = useWorld();
  const [characters] = useTable(tables.character);
  const [users] = useTable(tables.user);
  const entityMap = useRef<Map<string, Entity>>(new Map());

  // Cleanup on unmount: destroy every Koota entity we created. Same
  // reasoning as SegmentBridge — the world singleton outlives this
  // component's mount cycle, so we have to clean up explicitly.
  useEffect(() => {
    return () => {
      for (const entity of entityMap.current.values()) {
        try {
          entity.destroy();
        } catch {
          /* ignore */
        }
      }
      entityMap.current.clear();
    };
  }, []);

  // Index user rows by userId (string for Map key) for the join.
  const usersByUserId = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const u of users) m.set(u.userId.toString(), u.name ?? undefined);
    return m;
  }, [users]);

  useEffect(() => {
    const seen = new Set<string>();
    for (const c of characters) {
      const charIdHex = c.identity.toHexString();
      const userIdStr = c.userId.toString();
      seen.add(charIdHex);
      const isMine = charIdHex === myIdentity;
      const existing = entityMap.current.get(charIdHex);
      const name = usersByUserId.get(userIdStr) ?? '…';

      const pose = {
        segmentId: c.segmentId.toString(),
        s: c.s,
        theta: c.theta,
        r: c.r,
      };

      if (existing) {
        if (!isMine) existing.set(Pose, pose);
        existing.set(CharacterName, { value: name });
      } else {
        const entity = world.spawn(
          Pose(pose),
          CharacterName({ value: name }),
          CharacterIdentity({ value: charIdHex }),
          CharacterUserId({ value: userIdStr })
        );
        entity.add(IsCharacter);
        if (isMine) entity.add(IsLocal);
        entityMap.current.set(charIdHex, entity);
      }
    }
    for (const [id, entity] of entityMap.current) {
      if (!seen.has(id)) {
        entity.destroy();
        entityMap.current.delete(id);
      }
    }
  }, [characters, usersByUserId, myIdentity, world]);

  return null;
}

// ---------------------------------------------------------------------------
// Input + camera + rendering — operate on Character entities (was Player)
// ---------------------------------------------------------------------------

const CLIMB_SPEED = 5; // units / second along axis
const SPIRAL_SPEED = 2.2; // radians / second around axis
const SEND_INTERVAL_MS = 100;

// Camera pitch bounds & wheel step. Shared between InputController (wheel +
// two-finger touch update CameraPitch trait) and FollowCamera (reads it).
const PITCH_DEFAULT = (48 * Math.PI) / 180;
const PITCH_MIN = (28 * Math.PI) / 180;
const PITCH_MAX = (75 * Math.PI) / 180;
const PITCH_STEP_WHEEL = (3 * Math.PI) / 180; // per wheel notch
const PITCH_TOUCH_GAIN = 0.003; // radians per pixel of two-finger drag

// Single-finger virtual joystick. Y axis only — the X axis is now
// raycast-driven (see RaycastTouch handling in InputController). Drags
// below DEAD_ZONE produce no input; drags beyond FULL_THROTTLE saturate
// at ±1.
const DEAD_ZONE_PX = 12;
const FULL_THROTTLE_PX = 90;

// Exponential-smoothing time constant for the raycast target theta. The
// target snaps to wherever the finger is pointing; this lerp eases the
// squirrel toward it so micro-jitter doesn't translate to micro-twitch.
// Small enough that the squirrel feels glued to the finger.
const TAU_STICK_THETA = 0.06;

function stickInput(rawDelta: number, dz: number, full: number): number {
  const sign = Math.sign(rawDelta);
  const m = Math.abs(rawDelta);
  if (m < dz) return 0;
  if (m > full) return sign;
  return (sign * (m - dz)) / (full - dz);
}

function lookupSegment(
  segments: readonly Entity[],
  segmentEntities: Map<string, Entity>,
  segmentId: string
): Entity | undefined {
  const explicit = segmentEntities.get(segmentId);
  if (explicit) return explicit;
  return segments[0];
}

type SentPose = { segmentId: string; s: number; theta: number; r: number; t: number };

function InputController({
  segmentEntities,
  childrenMap,
}: {
  segmentEntities: Map<string, Entity>;
  childrenMap: Map<string, string[]>;
}) {
  const localEntities = useQuery(IsLocal, Pose);
  const segments = useQuery(SegmentGeometry);
  const local = localEntities[0];
  const setPose = useReducer(reducers.updatePose);
  const keys = useRef<Set<string>>(new Set());
  const canvasEl = useThree(state => state.gl.domElement);
  const camera = useThree(state => state.camera);
  // Current single-finger touch position in canvas pixel coordinates,
  // updated by the touch handler and read by useFrame for the per-frame
  // raycast. Null when not in single-finger drag mode.
  const touchScreenPos = useRef<{ x: number; y: number } | null>(null);
  // Scratch THREE objects to avoid per-frame allocation in the raycast.
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const lastSent = useRef<SentPose>({
    segmentId: '',
    s: 0,
    theta: 0,
    r: 0,
    t: 0,
  });

  // Keyboard — window-level so focus stays anywhere on the page.
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => keys.current.add(e.key.toLowerCase());
    const onUp = (e: KeyboardEvent) => keys.current.delete(e.key.toLowerCase());
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Wheel — canvas-only, so scrolling over the in-game menu still works.
  // Adjusts CameraPitch on the session entity.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      const cur = session.get(CameraPitch)?.value ?? PITCH_DEFAULT;
      const next = clamp(
        cur + dir * PITCH_STEP_WHEEL,
        PITCH_MIN,
        PITCH_MAX
      );
      session.set(CameraPitch, { value: next });
    };
    canvasEl.addEventListener('wheel', onWheel, { passive: false });
    return () => canvasEl.removeEventListener('wheel', onWheel);
  }, [canvasEl]);

  // Touch — single-finger = raycast positioning (per-issue #9: pixel-
  // perfect θ tracking via ray-into-trunk), with Y as velocity-add drag.
  // Two-finger vertical drag = camera pitch. Both attach to the canvas
  // so UI overlays (HUD / InGameMenu) keep their own pointer events.
  useEffect(() => {
    type Touched = { curX: number; curY: number };
    const touches = new Map<number, Touched>();
    // Y stick anchor — first touch position's Y. Y axis is still
    // velocity-driven (drag up = climb up); X is raycast-driven, no
    // anchor needed (your finger *is* the cursor on the trunk).
    let stickAnchorY: number | null = null;
    let pitchAnchor: { avgY: number; basePitch: number } | null = null;

    const reclassify = () => {
      const count = touches.size;
      if (count === 1) {
        pitchAnchor = null;
        const t = touches.values().next().value!;
        // Re-anchor Y every time we (re)enter single-finger mode, so
        // coming back from a two-finger gesture doesn't jolt the climb.
        stickAnchorY = t.curY;
        touchScreenPos.current = { x: t.curX, y: t.curY };
      } else if (count === 2) {
        stickAnchorY = null;
        touchScreenPos.current = null;
        const arr = Array.from(touches.values());
        const avgY = (arr[0].curY + arr[1].curY) / 2;
        const basePitch = session.get(CameraPitch)?.value ?? PITCH_DEFAULT;
        pitchAnchor = { avgY, basePitch };
      } else {
        stickAnchorY = null;
        pitchAnchor = null;
        touchScreenPos.current = null;
      }
      // Whenever the mode changes, reset the joystick state so a stale
      // input doesn't drive the squirrel during gesture switching.
      session.set(TouchJoystick, { y: 0, active: false });
    };

    const drive = () => {
      if (stickAnchorY !== null && touches.size === 1) {
        const t = touches.values().next().value!;
        touchScreenPos.current = { x: t.curX, y: t.curY };
        const dy = t.curY - stickAnchorY;
        const stickY = stickInput(dy, DEAD_ZONE_PX, FULL_THROTTLE_PX);
        // Active stays true while a single finger is down so useFrame
        // keeps raycasting (x axis lives outside this trait now).
        session.set(TouchJoystick, {
          y: -stickY,
          active: true,
        });
      }
      if (pitchAnchor && touches.size === 2) {
        const arr = Array.from(touches.values());
        const avgY = (arr[0].curY + arr[1].curY) / 2;
        // Drag up (avgY decreases) → pitch increases (more vertical view).
        const dy = pitchAnchor.avgY - avgY;
        const next = clamp(
          pitchAnchor.basePitch + dy * PITCH_TOUCH_GAIN,
          PITCH_MIN,
          PITCH_MAX
        );
        session.set(CameraPitch, { value: next });
      }
    };

    const onStart = (e: TouchEvent) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        touches.set(t.identifier, { curX: t.clientX, curY: t.clientY });
      }
      reclassify();
      drive();
    };
    const onMove = (e: TouchEvent) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        const existing = touches.get(t.identifier);
        if (existing) {
          existing.curX = t.clientX;
          existing.curY = t.clientY;
        }
      }
      drive();
    };
    const onEnd = (e: TouchEvent) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        touches.delete(t.identifier);
      }
      reclassify();
      drive();
    };

    canvasEl.addEventListener('touchstart', onStart, { passive: false });
    canvasEl.addEventListener('touchmove', onMove, { passive: false });
    canvasEl.addEventListener('touchend', onEnd, { passive: false });
    canvasEl.addEventListener('touchcancel', onEnd, { passive: false });
    return () => {
      canvasEl.removeEventListener('touchstart', onStart);
      canvasEl.removeEventListener('touchmove', onMove);
      canvasEl.removeEventListener('touchend', onEnd);
      canvasEl.removeEventListener('touchcancel', onEnd);
      session.set(TouchJoystick, { y: 0, active: false });
    };
  }, [canvasEl]);

  useFrame((_, delta) => {
    if (!local) return;
    const cur = local.get(Pose);
    if (!cur) return;
    const segmentEntity = lookupSegment(segments, segmentEntities, cur.segmentId);
    if (!segmentEntity) return;
    const seg = segmentEntity.get(SegmentGeometry);
    const links = segmentEntity.get(SegmentLinks);
    if (!seg || !links) return;

    // Keyboard contributes velocity to both axes.
    let dSKeyboard = 0;
    let dThetaKeyboard = 0;
    if (keys.current.has('arrowup') || keys.current.has('w')) dSKeyboard += 1;
    if (keys.current.has('arrowdown') || keys.current.has('s')) dSKeyboard -= 1;
    if (keys.current.has('arrowleft') || keys.current.has('a')) dThetaKeyboard -= 1;
    if (keys.current.has('arrowright') || keys.current.has('d')) dThetaKeyboard += 1;

    const stick = session.get(TouchJoystick);
    const stickActive = stick?.active ?? false;
    const stickY = stick?.y ?? 0;

    // Y (axial): keyboard + stick Y, both velocity-add.
    const dS = clamp(dSKeyboard + (stickActive ? stickY : 0), -1, 1);

    // X (spiral): raycast positioning. Three regimes, all driven by the
    // same ray from camera through the touch point:
    //
    //   1. Ray hits the segment cylinder within [0, length] →
    //      pixel-perfect: target θ = hit angle, ease toward it.
    //   2. Ray misses the cylinder but its closest approach to the axis
    //      is ≤ radius → finger is above/below the segment (climbing
    //      regime). Hold θ. This is the "no bounce" case — we used to
    //      fall back to plane-projection here, which snapped θ toward
    //      an arbitrary angle when the user dragged up/down off the
    //      trunk top/bottom.
    //   3. Ray misses the cylinder AND closest approach > radius →
    //      finger is past the silhouette. Rotate at SPIRAL_SPEED toward
    //      the silhouette angle (same rate as the old anchor model's
    //      saturation, so users can hold finger past the trunk edge to
    //      rotate around the back without needing edge-of-screen room).
    //
    // Keyboard rotates θ when no finger is positioning.
    let nextTheta = cur.theta;
    const pos = touchScreenPos.current;
    if (stickActive && pos) {
      const rect = canvasEl.getBoundingClientRect();
      ndc.x = ((pos.x - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((pos.y - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const origin: [number, number, number] = [
        raycaster.ray.origin.x,
        raycaster.ray.origin.y,
        raycaster.ray.origin.z,
      ];
      const dir: [number, number, number] = [
        raycaster.ray.direction.x,
        raycaster.ray.direction.y,
        raycaster.ray.direction.z,
      ];
      const cylTheta = raycastSegmentTheta(origin, dir, seg);
      if (cylTheta !== null) {
        const angDelta = wrapAngle(cylTheta - cur.theta);
        const alpha = 1 - Math.exp(-delta / TAU_STICK_THETA);
        nextTheta = cur.theta + angDelta * alpha;
      } else {
        const camPos: [number, number, number] = [
          camera.position.x,
          camera.position.y,
          camera.position.z,
        ];
        const sat = raySegmentSaturationDirection(origin, dir, camPos, seg);
        if (sat !== 0) {
          nextTheta = cur.theta + sat * SPIRAL_SPEED * delta;
        }
        // Else: above/below the segment — hold cur.theta (no bounce).
      }
    } else {
      nextTheta =
        cur.theta + clamp(dThetaKeyboard, -1, 1) * SPIRAL_SPEED * delta;
    }

    const thetaChanged = Math.abs(nextTheta - cur.theta) > 1e-9;
    if (dS !== 0 || thetaChanged) {
      let nextSegmentId = cur.segmentId;
      let nextS = cur.s + dS * CLIMB_SPEED * delta;
      let activeSeg = seg;

      // Climbing past the tip: hop to a child segment. At junctions
      // pick the child whose direction most aligns with the parent's
      // — biases toward continuing along the trunk over wandering
      // onto thin lateral leaf-branches. For a length-0 root segment
      // `unitAxis` falls back to (0,1,0) UP, which still picks the
      // upward-going trunk child over lateral fan-outs. A future spec
      // may add theta-based steering so the player can choose at a
      // junction by where they're facing.
      if (nextS > activeSeg.length) {
        const childIds = childrenMap.get(cur.segmentId) ?? [];
        const parentDir = unitAxis(activeSeg);
        let bestId: string | undefined;
        let bestGeom: ReturnType<Entity['get']> | undefined;
        let bestScore = -Infinity;
        for (const cid of childIds) {
          const ce = segmentEntities.get(cid);
          const cg = ce?.get(SegmentGeometry);
          if (!cg) continue;
          const cd = unitAxis(cg);
          const score =
            parentDir[0] * cd[0] + parentDir[1] * cd[1] + parentDir[2] * cd[2];
          if (score > bestScore) {
            bestScore = score;
            bestId = cid;
            bestGeom = cg;
          }
        }
        if (bestId && bestGeom) {
          nextS -= activeSeg.length;
          nextSegmentId = bestId;
          activeSeg = bestGeom;
        }
      }

      // Descending below the base: hop to parent.
      if (nextS < 0 && links.parentSegmentId) {
        const parentEntity = segmentEntities.get(links.parentSegmentId);
        const parentGeom = parentEntity?.get(SegmentGeometry);
        if (parentEntity && parentGeom) {
          nextS += parentGeom.length;
          nextSegmentId = links.parentSegmentId;
          activeSeg = parentGeom;
        }
      }

      // Clamp to whatever segment we ended up on.
      nextS = clamp(nextS, 0, activeSeg.length);

      local.set(Pose, {
        segmentId: nextSegmentId,
        s: nextS,
        theta: nextTheta,
        r: cur.r,
      });
    }

    const now = performance.now();
    if (now - lastSent.current.t > SEND_INTERVAL_MS) {
      const next = local.get(Pose)!;
      if (
        next.segmentId !== lastSent.current.segmentId ||
        next.s !== lastSent.current.s ||
        next.theta !== lastSent.current.theta ||
        next.r !== lastSent.current.r
      ) {
        const segIdBigint = next.segmentId ? BigInt(next.segmentId) : 0n;
        setPose({
          segmentId: segIdBigint,
          s: next.s,
          theta: next.theta,
          r: next.r,
        });
        lastSent.current = {
          segmentId: next.segmentId,
          s: next.s,
          theta: next.theta,
          r: next.r,
          t: now,
        };
      }
    }
  });

  return null;
}

function CharacterSphere({
  entity,
  segmentEntities,
}: {
  entity: Entity;
  segmentEntities: Map<string, Entity>;
}) {
  const pose = useTrait(entity, Pose);
  const isLocal = useHas(entity, IsLocal);
  const segments = useQuery(SegmentGeometry);
  const segmentEntity = pose
    ? lookupSegment(segments, segmentEntities, pose.segmentId)
    : undefined;
  const seg = useTrait(segmentEntity, SegmentGeometry);

  if (!pose || !seg) return null;

  const [x, y, z] = segmentToWorld(seg as SegmentDef, pose.s, pose.theta, pose.r);
  return (
    <mesh position={[x, y, z]} castShadow>
      <sphereGeometry args={[0.4, 32, 16]} />
      <meshStandardMaterial
        color={isLocal ? scene.characterLocal : scene.characterRemote}
      />
    </mesh>
  );
}

function Characters({ segmentEntities }: { segmentEntities: Map<string, Entity> }) {
  const entities = useQuery(IsCharacter, Pose);
  return (
    <>
      {entities.map(entity => (
        <CharacterSphere
          key={entity}
          entity={entity}
          segmentEntities={segmentEntities}
        />
      ))}
    </>
  );
}

const KNOT_COUNT = 18;
type Knot = { s: number; theta: number; size: number };

function generateKnots(length: number): Knot[] {
  const knots: Knot[] = [];
  for (let i = 0; i < KNOT_COUNT; i++) {
    const r1 = ((i * 9301 + 49297) % 233280) / 233280;
    const r2 = (((i + 7) * 99991 + 7919) % 65521) / 65521;
    const r3 = (((i + 13) * 13183 + 3613) % 49157) / 49157;
    knots.push({
      s: 0.5 + r1 * (length - 1),
      theta: r2 * Math.PI * 2,
      size: 0.08 + r3 * 0.13,
    });
  }
  return knots;
}

function Trunk({ entity }: { entity: Entity }) {
  const seg = useTrait(entity, SegmentGeometry);
  const links = useTrait(entity, SegmentLinks);

  // For v0 we render every segment as a tapered cylinder along the
  // straight line p0 → p3 (degenerate beziers from lib/tree-gen).
  // Slice 4 will switch trunk-shaped segments to a TubeGeometry that
  // honors the bezier curve for non-degenerate cases.
  const center = useMemo<[number, number, number]>(() => {
    if (!seg) return [0, 0, 0];
    return [
      (seg.p0X + seg.p3X) / 2,
      (seg.p0Y + seg.p3Y) / 2,
      (seg.p0Z + seg.p3Z) / 2,
    ];
  }, [seg?.p0X, seg?.p0Y, seg?.p0Z, seg?.p3X, seg?.p3Y, seg?.p3Z]);

  const quat = useMemo(() => {
    if (!seg) return new THREE.Quaternion();
    const dir = new THREE.Vector3(
      seg.p3X - seg.p0X,
      seg.p3Y - seg.p0Y,
      seg.p3Z - seg.p0Z
    );
    if (dir.lengthSq() < 1e-12) return new THREE.Quaternion();
    return new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      dir.normalize()
    );
  }, [seg?.p0X, seg?.p0Y, seg?.p0Z, seg?.p3X, seg?.p3Y, seg?.p3Z]);

  // Trunk-only knots: only the root segment (no parent) gets them
  // for now. Branches stay smooth. Slice 4 may rework knots.
  const isRoot = links?.parentSegmentId === '';
  const knots = useMemo(
    () => (seg && isRoot ? generateKnots(seg.length) : []),
    [seg?.length, isRoot]
  );

  if (!seg || !links || seg.length <= 0) return null;

  const color = isRoot ? scene.trunk : scene.branch;

  return (
    <>
      <mesh position={center} quaternion={quat} castShadow receiveShadow>
        <cylinderGeometry
          args={[seg.radiusTip, seg.radiusBase, seg.length, 24, 1]}
        />
        <meshStandardMaterial color={color} roughness={0.9} metalness={0} />
      </mesh>
      {knots.map((k, i) => {
        const [x, y, z] = segmentToWorld(
          seg as SegmentDef,
          k.s,
          k.theta,
          k.size * 0.4
        );
        return (
          <mesh key={i} position={[x, y, z]} castShadow>
            <sphereGeometry args={[k.size, 10, 8]} />
            <meshStandardMaterial color={scene.knot} roughness={1} />
          </mesh>
        );
      })}
    </>
  );
}

function Trunks() {
  const entities = useQuery(IsSegment);
  return (
    <>
      {entities.map(e => (
        <Trunk key={e} entity={e} />
      ))}
    </>
  );
}

// Chase camera tunables. See DOCS/SPECS/warm_atmosphere_and_chase_camera.md.
//
// Camera frame is a boom-arm: fixed-length BOOM_LENGTH from the
// squirrel-anchor, oriented in the (out, axis) plane. PITCH is the
// angle the view direction makes above horizontal (perpendicular to
// the segment axis). SQUIRREL_OFFSET is the constant angle the
// squirrel sits below view-center — this is what locks the squirrel's
// vertical screen position. As pitch changes (wheel or two-finger
// drag — both write to CameraPitch on the session entity), both the
// camera position and view direction rotate around the squirrel
// together; the screen-y of the squirrel stays fixed.
const BOOM_LENGTH = 5.41; // ~sqrt(4.5² + 3²); preserves prior framing
const SQUIRREL_OFFSET = (14 * Math.PI) / 180; // squirrel at ~bottom 1/4
const DEADBAND = (35 * Math.PI) / 180;
const TAU_POS = 0.12;
const TAU_LOOK = 0.15;
const TAU_THETA = 0.2;

function wrapAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= 2 * Math.PI;
  while (x < -Math.PI) x += 2 * Math.PI;
  return x;
}

function unitAxis(seg: SegmentDef): [number, number, number] {
  const dx = seg.p3X - seg.p0X;
  const dy = seg.p3Y - seg.p0Y;
  const dz = seg.p3Z - seg.p0Z;
  const len = Math.hypot(dx, dy, dz);
  if (len === 0) return [0, 1, 0];
  return [dx / len, dy / len, dz / len];
}

/**
 * Chase camera. Sits behind-and-below the local squirrel along the
 * segment axis, looking at a point ahead up the trunk.
 *
 * Has its own `cameraTheta` state with an angular deadband: small
 * spirals slide the squirrel laterally within the frame; only
 * sustained spiral input pushes the camera around the trunk. Position
 * and look target are exponentially smoothed.
 */
function FollowCamera({
  segmentEntities,
}: {
  segmentEntities: Map<string, Entity>;
}) {
  const localEntities = useQuery(IsLocal, Pose);
  const local = localEntities[0];
  const segments = useQuery(SegmentGeometry);

  // Persisted camera state. Pitch is owned by the session entity (written
  // by mouse wheel and two-finger touch in InputController, read here).
  const cameraTheta = useRef<number | null>(null);
  const camPos = useRef(new THREE.Vector3());
  const camLook = useRef(new THREE.Vector3());
  const initialized = useRef(false);

  useFrame(({ camera }, delta) => {
    if (!local) return;
    const pose = local.get(Pose);
    if (!pose) return;
    const segmentEntity = lookupSegment(
      segments,
      segmentEntities,
      pose.segmentId
    );
    if (!segmentEntity) return;
    const seg = segmentEntity.get(SegmentGeometry);
    if (!seg) return;

    // Start with no lag on first frame.
    if (cameraTheta.current === null) cameraTheta.current = pose.theta;

    // Angular deadband + ease.
    // Δ = signed offset between squirrel θ and camera θ. While |Δ| ≤
    // DEADBAND we leave cameraTheta alone — the squirrel drifts within
    // the visible cone. Outside the band we ease cameraTheta so the
    // squirrel sits at the band edge in steady state (does not snap
    // back to center).
    const angDelta = wrapAngle(pose.theta - cameraTheta.current);
    if (Math.abs(angDelta) > DEADBAND) {
      const targetTheta = pose.theta - Math.sign(angDelta) * DEADBAND;
      const alpha = 1 - Math.exp(-delta / TAU_THETA);
      const step = wrapAngle(targetTheta - cameraTheta.current) * alpha;
      cameraTheta.current = cameraTheta.current + step;
    }

    // Frame the camera using cameraTheta as the radial direction so
    // the camera lags the squirrel when sustained spiral input pushes
    // past the deadband.
    const segDef = seg as SegmentDef;
    const out = radialOutward(segDef, cameraTheta.current);
    const axisUnit = unitAxis(segDef);

    // Anchor on the trunk at the squirrel's axial s and r, but at the
    // *camera's* θ. Within the deadband this means the camera does not
    // translate laterally with the squirrel — the squirrel slides
    // through the frame instead of staying frame-centered.
    const anchor = segmentToWorld(
      segDef,
      pose.s,
      cameraTheta.current,
      pose.r
    );

    // Boom-arm geometry. γ is the angle of the cam→squirrel-anchor
    // direction below horizontal (perpendicular to axis). For the
    // squirrel to sit at SQUIRREL_OFFSET below view-center, γ = pitch
    // − SQUIRREL_OFFSET. Camera is at fixed BOOM_LENGTH from anchor.
    const phi =
      (session.get(CameraPitch)?.value as number | undefined) ?? PITCH_DEFAULT;
    const gamma = phi - SQUIRREL_OFFSET;
    const cosG = Math.cos(gamma);
    const sinG = Math.sin(gamma);
    const cosP = Math.cos(phi);
    const sinP = Math.sin(phi);

    const targetPos = new THREE.Vector3(
      anchor[0] + BOOM_LENGTH * (cosG * out[0] - sinG * axisUnit[0]),
      anchor[1] + BOOM_LENGTH * (cosG * out[1] - sinG * axisUnit[1]),
      anchor[2] + BOOM_LENGTH * (cosG * out[2] - sinG * axisUnit[2])
    );

    // View direction: angled `phi` above horizontal, looking inward
    // toward the trunk. Look point is camera + viewDir * BOOM_LENGTH
    // (any positive distance works — picking BOOM_LENGTH keeps the
    // smoothing target stable in scale).
    const targetLook = new THREE.Vector3(
      targetPos.x + BOOM_LENGTH * (-cosP * out[0] + sinP * axisUnit[0]),
      targetPos.y + BOOM_LENGTH * (-cosP * out[1] + sinP * axisUnit[1]),
      targetPos.z + BOOM_LENGTH * (-cosP * out[2] + sinP * axisUnit[2])
    );

    if (!initialized.current) {
      camPos.current.copy(targetPos);
      camLook.current.copy(targetLook);
      initialized.current = true;
    } else {
      camPos.current.lerp(targetPos, 1 - Math.exp(-delta / TAU_POS));
      camLook.current.lerp(targetLook, 1 - Math.exp(-delta / TAU_LOOK));
    }

    camera.position.copy(camPos.current);
    camera.lookAt(camLook.current);
  });

  return null;
}

function HUD() {
  const conn = useSpacetimeDB();
  const [characters] = useTable(tables.character);

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 10,
        color: 'white',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 13,
        background: 'rgba(0,0,0,0.4)',
        padding: '8px 12px',
        borderRadius: 6,
        pointerEvents: 'none',
      }}
    >
      <div>
        Status:{' '}
        <strong style={{ color: conn?.isActive ? '#7fcd91' : '#cd7f7f' }}>
          {conn?.isActive ? 'Connected' : 'Disconnected'}
        </strong>
      </div>
      <div>In the canopy: {characters.length}</div>
      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>
        ↑/W climb &nbsp; ↓/S descend &nbsp; ←/A spiral L &nbsp; →/D spiral R
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Game shell — the rendered scene + bridges + HUD. Renders unconditionally
// inside SessionGuard's CONNECTED states so the world is visible even when
// EnterCanopy is overlaid (lets new visitors preview the canopy).
// ---------------------------------------------------------------------------

function GameShell() {
  const segmentEntities = useRef<Map<string, Entity>>(new Map()).current;
  const sessionState = useSessionState();
  const isPlaying = sessionState === 'PLAYING';

  // Children index for traversal — segmentId → first-child segmentId.
  // For v0 we pick the first child encountered when climbing past a
  // junction; a future spec may let the player choose at junctions.
  const [segments] = useTable(tables.segment);
  const childrenMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const seg of segments) {
      if (seg.parentSegmentId === 0n) continue;
      const parentKey = seg.parentSegmentId.toString();
      const arr = m.get(parentKey) ?? [];
      arr.push(seg.segmentId.toString());
      m.set(parentKey, arr);
    }
    return m;
  }, [segments]);

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <SegmentBridge segmentEntities={segmentEntities} />
      <CharacterBridge />
      <HUD />
      {isPlaying && <InGameMenu />}
      <Canvas
        shadows
        camera={{ position: [10, 12, 10], fov: 55 }}
        style={{ background: scene.sky }}
      >
        <fog attach="fog" args={[scene.fog, scene.fogNear, scene.fogFar]} />
        <ambientLight
          color={scene.ambientColor}
          intensity={scene.ambientIntensity}
        />
        <directionalLight
          color={scene.directionalColor}
          position={[15, 20, 10]}
          intensity={scene.directionalIntensity}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={1}
          shadow-camera-far={60}
          shadow-camera-left={-20}
          shadow-camera-right={20}
          shadow-camera-top={20}
          shadow-camera-bottom={-20}
        />
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, -0.001, 0]}
          receiveShadow
        >
          <planeGeometry args={[60, 60]} />
          <meshStandardMaterial color={scene.ground} />
        </mesh>
        <Trunks />
        <Characters segmentEntities={segmentEntities} />
        {isPlaying && (
          <InputController
            segmentEntities={segmentEntities}
            childrenMap={childrenMap}
          />
        )}
        {isPlaying && <FollowCamera segmentEntities={segmentEntities} />}
      </Canvas>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level routing — / vs /play. Keep simple; introduce a router later if
// the surface grows.
// ---------------------------------------------------------------------------

function App() {
  const path = usePath();
  if (path === '/' || path === '/index.html' || path === '') {
    return <LandingPage />;
  }
  const inTools = path.startsWith('/tools');
  return (
    <SessionGuard>
      {inTools ? <ToolsRoute /> : <GameShell />}
    </SessionGuard>
  );
}

export default App;
